import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Row, Col, Form } from "react-bootstrap";

import "react-toastify/dist/ReactToastify.css";
import instance from "../../../services/endpoint.js";
import Loader from "../../utilComponents/loader.js";
import Shimmer from "../../utilComponents/shimmer.js";

const PaymentDetailsTab = () => {
  const [profileData, setProfileData] = useState({
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    bic_swift_code: "",
    branch: "",
    bank_account_currency: "",
    upi_address: "",
    btc_withdraw_address: "",
    eth_withdraw_address: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [shimmerLoading, setShimmerLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    document.title = "Profile | Richesse";
    const fetchProfileData = async () => {
      try {
        setShimmerLoading(true);
        const res = await instance.get("/api/v1/withdrawDetails");
        setProfileData(res.data.decryptedWithdrawData);
        setInitError(null);
      } catch (err) {
        toast.error(err.response?.data?.message);
        setInitError(err.response?.data?.msg);
        console.error("Error fetching profile data:", err);
      } finally {
        setShimmerLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "account_holder_name":
        if (value && !/^[A-Za-z\s]+$/.test(value)) {
          error = "Only letters and spaces allowed.";
        }
        break;

      case "account_number":
        const rawValue = value.replace(/\s+/g, "");
        if (rawValue && (!/^\d+$/.test(rawValue) || rawValue.length !== 16)) {
          error = "Account number must be exactly 16 digits.";
        }
        break;

      case "ifsc_code":
        if (value && !/^[A-Za-z]{4}\d{6,7}$/.test(value)) {
          error =
            "IFSC Code must start with 4 letters followed by 6 or 7 digits.";
        }
        break;

      case "bic_swift_code":
        if (value && !/^[A-Za-z0-9]{8,11}$/.test(value)) {
          error = "BIC/SWIFT code must be 8 to 11 alphanumeric characters.";
        }
        break;

      case "branch":
        if (value && !/^[A-Za-z0-9\s]+$/.test(value)) {
          error = "Branch name must be alphanumeric.";
        }
        break;

      case "upi_address":
        if (value && !/^[\w.]{2,256}@[a-zA-Z]{2,64}$/.test(value)) {
          error = "Invalid UPI address format.";
        }
        break;

      case "btc_withdraw_address":
        if (value && !/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(value)) {
          error = "Invalid BTC address.";
        }
        break;

      case "eth_withdraw_address":
        if (value && !/^0x[a-fA-F0-9]{40}$/.test(value)) {
          error = "Invalid ETH address.";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "account_number") {
      const raw = value.replace(/\s+/g, "");
      if (!/^\d*$/.test(raw)) return;
      formattedValue = raw.match(/.{1,4}/g)?.join(" ") || "";
    }

    const error = validateField(name, formattedValue);
    setErrors({ ...errors, [name]: error });
    setProfileData({ ...profileData, [name]: formattedValue });
  };

  const handleSubmit = async () => {
    const newErrors = {};
    Object.entries(profileData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please correct errors before submitting.");
      return;
    }

    try {
      setLoading(true);
      const payload = { withdrawData: profileData };
      const res = await instance.post("/api/v1/withdrawDetails", payload);
      toast.success(res.data.message || "Profile updated successfully!");
    } catch (err) {
      toast.error("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="container custom-container">
        {/* initial alert */}
        {initError &&
        <div class="alert alert-warning d-flex align-items-center" role="alert">
             {initError} 
        </div>
        }
        <Form>
          <Row className="mb-3">
            <Col xs={12}>
              <h5 className="text-secondary">Bank Account Details</h5>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Account Holder Name</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="account_holder_name"
                  value={profileData.account_holder_name}
                  onChange={handleChange}
                  // placeholder="Account Holder Name"
                  isInvalid={!!errors.account_holder_name}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.account_holder_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Account Number</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="account_number"
                  value={profileData.account_number}
                  onChange={handleChange}
                  // placeholder="Account Number"
                  isInvalid={!!errors.account_number}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.account_number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>IFSC Code</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="ifsc_code"
                  value={profileData.ifsc_code}
                  onChange={handleChange}
                  // placeholder="IFSC Code"
                  isInvalid={!!errors.ifsc_code}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.ifsc_code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>BIC/SWIFT Code</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="bic_swift_code"
                  value={profileData.bic_swift_code}
                  onChange={handleChange}
                  // placeholder="BIC/SWIFT Code"
                  isInvalid={!!errors.bic_swift_code}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.bic_swift_code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Branch</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="branch"
                  value={profileData.branch}
                  onChange={handleChange}
                  // placeholder="Branch"
                  isInvalid={!!errors.branch}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.branch}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Bank Account Currency</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Select
                  name="bank_account_currency"
                  value={profileData.bank_account_currency}
                  onChange={handleChange}>
                  <option value="">Select Currency</option>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="USDT">USDT</option>
                </Form.Select>
                )}
              </Form.Group>
            </Col>

            <Col xs={12}>
              <h5 className="text-secondary mt-4">UPI Details</h5>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>UPI Address</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="upi_address"
                  value={profileData.upi_address}
                  onChange={handleChange}
                  // placeholder="UPI Address"
                  isInvalid={!!errors.upi_address}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.upi_address}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <h5 className="text-secondary mt-4">Crypto Wallet</h5>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>BTC Withdraw Address</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="btc_withdraw_address"
                  value={profileData.btc_withdraw_address}
                  onChange={handleChange}
                  // placeholder="BTC Address"
                  isInvalid={!!errors.btc_withdraw_address}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.btc_withdraw_address}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ETH Withdraw Address</Form.Label>
                {shimmerLoading ? (
                            <Shimmer width="100%" height="38px" />
                          ) : (
                <Form.Control
                  type="text"
                  name="eth_withdraw_address"
                  value={profileData.eth_withdraw_address}
                  onChange={handleChange}
                  // placeholder="ETH Address"
                  isInvalid={!!errors.eth_withdraw_address}
                />
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.eth_withdraw_address}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="search-btn"
              onClick={handleSubmit}
              disabled={loading}>
              {loading ? (
                "Saving..."
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default PaymentDetailsTab;
